import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, Article } from "../../lib/supabase";
import { Button } from "../../components/ui/button";
import {
  SidebarToggle,
  useAdminLayout,
} from "../../components/admin/AdminLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import {
  Edit,
  Trash,
  Plus,
  Search,
  CheckCircle,
  XCircle,
  Menu,
  Image as ImageIcon,
  Upload,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";
import { useToast } from "../../hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";

// Định nghĩa kiểu dữ liệu cho errors và category
type FormErrorType = {
  title?: string;
  slug?: string;
  category_id?: string;
  content?: string;
  excerpt?: string;
  author?: string;
  image_url?: string;
  [key: string]: string | undefined;
};

type CategoryType = {
  id: string;
  name: string;
  slug?: string;
  [key: string]: string | undefined;
};

type SubcategoryType = {
  id: string;
  name: string;
  slug?: string;
  category_id: string;
  [key: string]: string | undefined;
};

const AdminArticles = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [subcategories, setSubcategories] = useState<SubcategoryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentArticle, setCurrentArticle] = useState<Partial<Article>>({});
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrorType>({});
  const [savingArticle, setSavingArticle] = useState(false);
  const [deletingArticle, setDeletingArticle] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalArticles, setTotalArticles] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMobile, setSidebarVisible } = useAdminLayout();

  useEffect(() => {
    loadArticles();
    loadCategories();
  }, [currentPage, pageSize]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      // Get total count first
      const { count, error: countError } = await supabase
        .from("articles")
        .select("*", { count: "exact", head: true });

      if (countError) throw countError;

      setTotalArticles(count || 0);
      setTotalPages(Math.ceil((count || 0) / pageSize));

      // Then get paginated data
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error("Error loading articles:", error);
      toast({
        title: "Lỗi tải dữ liệu",
        description: "Không thể tải danh sách bài viết",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    const { data: categoriesData, error: categoriesError } = await supabase
      .from("categories")
      .select("*")
      .order("name");

    if (categoriesError) {
      console.error("Error loading categories:", categoriesError);
      toast({
        title: "Lỗi tải dữ liệu",
        description: "Không thể tải danh sách danh mục",
        variant: "destructive",
      });
    } else {
      setCategories(categoriesData || []);
    }
  };

  const loadSubcategories = async (categoryId: string) => {
    const { data, error } = await supabase
      .from("subcategories")
      .select("*")
      .eq("category_id", categoryId)
      .order("name");

    if (error) {
      console.error("Error loading subcategories:", error);
      toast({
        title: "Lỗi tải dữ liệu",
        description: "Không thể tải danh sách danh mục con",
        variant: "destructive",
      });
    } else {
      setSubcategories(data || []);
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setCurrentArticle({
      ...currentArticle,
      category_id: categoryId,
      subcategory_id: undefined,
    });
    loadSubcategories(categoryId);
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      loadArticles();
      return;
    }

    // Reset to first page when searching
    setCurrentPage(1);

    // Local search for simplicity. In a real app, you might want to search on the server
    const filteredArticles = articles.filter(
      (article) =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.content.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setArticles(filteredArticles);
  };

  const handleEdit = (article: Article) => {
    navigate(`/admin/articles/edit/${article.id}`);
    setIsMobileMenuOpen(false);
  };

  const handleCreate = () => {
    navigate("/admin/articles/create");
    setIsMobileMenuOpen(false);
  };

  const handleDelete = (article: Article) => {
    setCurrentArticle(article);
    setIsDeleteDialogOpen(true);
    setIsMobileMenuOpen(false);
  };

  const validateForm = () => {
    const errors: FormErrorType = {};
    if (!currentArticle.title) errors.title = "Tiêu đề không được để trống";
    if (!currentArticle.slug) errors.slug = "Slug không được để trống";
    if (!currentArticle.category_id)
      errors.category_id = "Danh mục không được để trống";
    if (!currentArticle.content)
      errors.content = "Nội dung không được để trống";
    if (!currentArticle.excerpt) errors.excerpt = "Tóm tắt không được để trống";
    if (!currentArticle.author) errors.author = "Tác giả không được để trống";
    if (!currentArticle.image_url) errors.image_url = "Ảnh không được để trống";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d")
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "-");
  };

  const handleTitleChange = (title: string) => {
    setCurrentArticle({
      ...currentArticle,
      title,
      slug: generateSlug(title),
    });
  };

  const handleSaveArticle = async () => {
    if (!validateForm()) return;

    const isNew = !currentArticle.id;
    const now = new Date().toISOString();
    setSavingArticle(true);

    try {
      if (isNew) {
        const { data, error } = await supabase
          .from("articles")
          .insert({
            ...currentArticle,
            created_at: now,
            updated_at: now,
          })
          .select();

        if (error) throw error;
        setArticles([data[0], ...articles]);
        toast({
          title: "Thành công",
          description: "Đã thêm bài viết mới",
          variant: "default",
        });
      } else {
        const { data, error } = await supabase
          .from("articles")
          .update({
            ...currentArticle,
            updated_at: now,
          })
          .eq("id", currentArticle.id!)
          .select();

        if (error) throw error;
        setArticles(
          articles.map((article) =>
            article.id === currentArticle.id ? data[0] : article
          )
        );
        toast({
          title: "Thành công",
          description: "Đã cập nhật bài viết",
          variant: "default",
        });
      }

      setIsEditDialogOpen(false);
    } catch (error: Error | unknown) {
      console.error("Error saving article:", error);
      toast({
        title: "Lỗi",
        description: `Không thể ${isNew ? "thêm" : "cập nhật"} bài viết: ${
          error instanceof Error ? error.message : "Lỗi không xác định"
        }`,
        variant: "destructive",
      });
    } finally {
      setSavingArticle(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (!currentArticle.id) return;
      setDeletingArticle(true);

      const { error } = await supabase
        .from("articles")
        .delete()
        .eq("id", currentArticle.id);

      if (error) throw error;

      setArticles(
        articles.filter((article) => article.id !== currentArticle.id)
      );
      setIsDeleteDialogOpen(false);
      toast({
        title: "Thành công",
        description: "Đã xóa bài viết",
        variant: "default",
      });
    } catch (error: Error | unknown) {
      console.error("Error deleting article:", error);
      toast({
        title: "Lỗi",
        description: `Không thể xóa bài viết: ${
          error instanceof Error ? error.message : "Lỗi không xác định"
        }`,
        variant: "destructive",
      });
    } finally {
      setDeletingArticle(false);
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>
  ) => {
    let file: File | null = null;

    // Xử lý khi người dùng chọn file qua input
    if (
      "target" in event &&
      event.target instanceof HTMLInputElement &&
      event.target.files
    ) {
      file = event.target.files[0];
    }
    // Xử lý khi người dùng kéo thả file
    else if ("dataTransfer" in event && event.dataTransfer) {
      file = event.dataTransfer.files[0];
    }

    if (!file) {
      setUploadError("Vui lòng chọn một tệp tin ảnh");
      return;
    }

    // Kiểm tra type của file
    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/)) {
      setUploadError("Chỉ chấp nhận file hình ảnh (JPEG, PNG, GIF, WEBP)");
      return;
    }

    try {
      setUploadingImage(true);
      setUploadError(null);

      // Tạo tên file ngẫu nhiên để tránh trùng lặp
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random()
        .toString(36)
        .substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `articles/${fileName}`;

      // Tạo preview URL cho ảnh được chọn
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);

      // Upload ảnh lên Supabase Storage
      const { data, error } = await supabase.storage
        .from("images")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        throw error;
      }

      // Lấy URL public cho ảnh đã upload
      const { data: publicUrlData } = supabase.storage
        .from("images")
        .getPublicUrl(filePath);

      // Cập nhật state với đường dẫn ảnh
      setCurrentArticle({
        ...currentArticle,
        image_url: publicUrlData.publicUrl,
      });

      toast({
        title: "Thành công",
        description: "Đã tải ảnh lên",
        variant: "default",
      });
    } catch (error: Error | unknown) {
      console.error("Error uploading image:", error);
      setUploadError(
        error instanceof Error
          ? error.message
          : "Lỗi không xác định khi tải ảnh lên"
      );
      toast({
        title: "Lỗi",
        description: "Không thể tải ảnh lên",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    handleImageUpload(event);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Generate pagination numbers
  const paginationItems = useMemo(() => {
    const items = [];

    // Always show first page
    items.push(
      <PaginationItem key="page-1">
        <PaginationLink
          isActive={currentPage === 1}
          onClick={() => handlePageChange(1)}
        >
          1
        </PaginationLink>
      </PaginationItem>
    );

    // If there are many pages, add ellipsis
    if (currentPage > 3) {
      items.push(
        <PaginationItem key="ellipsis-1">
          <span className="flex h-9 w-9 items-center justify-center">...</span>
        </PaginationItem>
      );
    }

    // Add pages around current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      if (i === 1 || i === totalPages) continue; // Skip first and last page as they're always shown
      items.push(
        <PaginationItem key={`page-${i}`}>
          <PaginationLink
            isActive={currentPage === i}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // If there are many pages, add ellipsis
    if (currentPage < totalPages - 2) {
      items.push(
        <PaginationItem key="ellipsis-2">
          <span className="flex h-9 w-9 items-center justify-center">...</span>
        </PaginationItem>
      );
    }

    // Always show last page if there's more than 1 page
    if (totalPages > 1) {
      items.push(
        <PaginationItem key={`page-${totalPages}`}>
          <PaginationLink
            isActive={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  }, [currentPage, totalPages]);

  return (
    <div className="w-full">
      <div className="flex items-center mb-6">
        {isMobile && <SidebarToggle className="mx-2" />}
        <h1 className="text-xl md:text-2xl font-semibold">Quản lý bài viết</h1>
      </div>

      {/* Controls cho desktop */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div className="hidden md:flex space-x-2">
          <Input
            placeholder="Tìm kiếm bài viết..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />
          <Button variant="outline" onClick={handleSearch}>
            <Search className="h-4 w-4" />
          </Button>
        </div>
        <Button onClick={handleCreate} className="hidden md:flex">
          <Plus className="h-4 w-4 mr-2" />
          Thêm bài viết
        </Button>

        {/* Sheet for mobile */}
        <Sheet>
          <div className="md:hidden w-full flex gap-2">
            <Input
              placeholder="Tìm kiếm..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
            <Button variant="outline" size="icon" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
            <Button size="icon" onClick={handleCreate}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </Sheet>
      </div>

      <Card className="w-full">
        <CardHeader className="p-4 md:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <CardTitle>Danh sách bài viết</CardTitle>
            <div>
              <Label htmlFor="page-size" className="mr-2">
                Hiển thị:
              </Label>
              <select
                id="page-size"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className="border rounded p-1"
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-6">
          {loading ? (
            <div className="text-center py-4">Đang tải...</div>
          ) : (
            <div className="w-full overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-medium">Tiêu đề</TableHead>
                    <TableHead className="hidden md:table-cell">Slug</TableHead>
                    <TableHead className="hidden sm:table-cell">
                      Tác giả
                    </TableHead>
                    <TableHead className="hidden sm:table-cell">
                      Ngày đăng
                    </TableHead>
                    <TableHead className="text-right">Hành động</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {articles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        Không có bài viết nào
                      </TableCell>
                    </TableRow>
                  ) : (
                    articles.map((article) => (
                      <TableRow key={article.id}>
                        <TableCell className="font-medium truncate max-w-[120px] sm:max-w-[200px] md:max-w-none">
                          {article.title}
                        </TableCell>
                        <TableCell className="hidden md:table-cell truncate max-w-[150px]">
                          {article.slug}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {article.author}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {new Date(article.date).toLocaleDateString("vi-VN")}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(article)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(article)}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        <CardFooter className="px-4 md:px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-muted-foreground text-center sm:text-left">
            Hiển thị {articles.length} trên tổng số {totalArticles} bài viết
          </div>
          <Pagination className="mt-0">
            <PaginationContent className="flex-wrap justify-center">
              {currentPage > 1 ? (
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => handlePageChange(currentPage - 1)}
                  />
                </PaginationItem>
              ) : (
                <PaginationItem>
                  <span className="opacity-50 cursor-not-allowed flex h-10 items-center justify-center gap-1 px-4">
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">Trước</span>
                  </span>
                </PaginationItem>
              )}

              {/* Hiển thị theo cách phù hợp với màn hình nhỏ */}
              <div className="hidden sm:flex">{paginationItems}</div>
              <div className="sm:hidden flex items-center mx-2">
                <span className="text-sm">
                  Trang {currentPage}/{totalPages}
                </span>
              </div>

              {currentPage < totalPages ? (
                <PaginationItem>
                  <PaginationNext
                    onClick={() => handlePageChange(currentPage + 1)}
                  />
                </PaginationItem>
              ) : (
                <PaginationItem>
                  <span className="opacity-50 cursor-not-allowed flex h-10 items-center justify-center gap-1 px-4">
                    <span className="hidden sm:inline">Tiếp</span>
                    <ChevronRight className="h-4 w-4" />
                  </span>
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        </CardFooter>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent className="w-[95%] md:w-[85%] lg:w-[450px] mx-auto p-4 sm:p-6">
          <AlertDialogHeader className="px-0 sm:px-0">
            <AlertDialogTitle>Xác nhận xóa bài viết</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc chắn muốn xóa bài viết "{currentArticle.title}" không?
              Hành động này không thể hoàn tác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 mt-2 pt-2 border-t">
            <AlertDialogCancel
              disabled={deletingArticle}
              className="w-full sm:w-auto"
            >
              Hủy
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deletingArticle}
              className="flex items-center bg-red-500 hover:bg-red-600 w-full sm:w-auto"
            >
              {deletingArticle ? (
                <>Đang xóa...</>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Xóa
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminArticles;
